import { translate, translateRich, useLocale } from "../../localization";
// src/components/answers/ScoreVisualizations.js
import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Cell,
} from "recharts";
import { AppButton } from "../ui";

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

function makeBins(rows, binSize, getScore, getAnswer, sampleCount = 3) {
  const binCount = Math.ceil(100 / binSize);

  const bins = Array.from({ length: binCount }, (_, i) => {
    const min = i * binSize;
    const max = i === binCount - 1 ? 100 : (i + 1) * binSize;
    return {
      key: `${min}-${max}`,
      min,
      max,
      range: `${min}–${max}`,
      count: 0,
      samples: [],
    };
  });

  for (const r of rows || []) {
    const s0 = getScore(r);
    if (s0 == null || !Number.isFinite(Number(s0))) continue;

    const s = clamp(Number(s0), 0, 100);
    let idx = Math.floor(s / binSize);
    if (s === 100) idx = binCount - 1;
    idx = clamp(idx, 0, binCount - 1);

    bins[idx].count += 1;

    const ans = (getAnswer(r) || "").toString().trim();
    if (ans && bins[idx].samples.length < sampleCount) {
      bins[idx].samples.push(ans);
    }
  }

  return bins;
}

function niceSnippet(s, n = 160) {
  const t = (s || "").toString().trim();
  if (!t) return "";
  return t.length > n ? t.slice(0, n) + "…" : t;
}

function ScoreTooltip({ active, payload }) {
  useLocale();
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;

  return (
    <div
      style={{
        background: "var(--color-bg-card, #ffffff)",
        border: "1px solid var(--color-border-subtle, rgba(0,0,0,0.12))",
        borderRadius: 16,
        padding: 14,
        boxShadow: "var(--shadow-card-hover, 0 4px 16px rgba(0, 0, 0, 0.06))",
        maxWidth: 380,
        color: "var(--color-text-primary, #1a1a2e)",
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 6 }}>{translateRich("Score {value0}", {
  value0: d.range
})}</div>

      <div
        style={{
          color: "var(--color-text-secondary, rgba(0,0,0,0.65))",
          fontWeight: 500,
          marginBottom: d.samples?.length ? 12 : 0,
        }}
      >{translateRich("Responses: {value0}", {value0: <span style={{
    fontWeight: 700,
    color: "var(--color-text-primary, #1a1a2e)"
  }}>
          {d.count}
        </span>})}</div>

      {d.samples?.length ? (
        <div style={{ fontSize: 14, lineHeight: 1.35 }}>
          <div
            style={{
              fontWeight: 600,
              letterSpacing: "0.10em",
              textTransform: "uppercase",
              color: "var(--color-text-secondary, rgba(0,0,0,0.65))",
              fontSize: 12,
              marginBottom: 10,
            }}
          >{translate("Sample answers")}</div>

          {d.samples.map((s, i) => (
            <div
              key={i}
              style={{
                marginBottom: 10,
                paddingLeft: 12,
                borderLeft: "3px solid var(--color-action-primary-soft, rgba(35, 159, 198, 0.24))",
                whiteSpace: "normal",
                color: "var(--color-text-primary, #1a1a2e)",
                fontWeight: 500,
              }}
            >
              {niceSnippet(s)}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Props:
 * - rows
 * - getAnswerText(row): string
 * - getScoreNumber(row): number|null
 * - selectedBin: {min,max} or null
 * - setSelectedBin: fn
 * - binSize: number
 * - avgScore?: number|null
 * - blankCount?: number
 */
export default function ScoreVisualizations({
  rows,
  getAnswerText,
  getScoreNumber,
  selectedBin,
  setSelectedBin,
  binSize = 20,
  avgScore = null,
  blankCount = 0,
  preserveUnscored = false,
}) {
  const locale = useLocale();
  const bins = useMemo(
    () => makeBins(rows, binSize, getScoreNumber, getAnswerText, 3),
    [rows, binSize, getScoreNumber, getAnswerText, locale]
  );

  const totalResponses = rows?.length || 0;

  const computedAvg = useMemo(() => {
    if (avgScore != null) return avgScore;
    if (!rows?.length) return null;

    const nums = rows
      .map((r) => getScoreNumber(r))
      .map((n) => (preserveUnscored && n == null ? null : Number.isFinite(Number(n)) ? Number(n) : null))
      .filter((n) => n != null);

    if (!nums.length) return null;
    const sum = nums.reduce((a, b) => a + b, 0);
    return Math.round(sum / nums.length);
  }, [avgScore, rows, getScoreNumber, preserveUnscored, locale]);

  return (
    <div className="qaViz">
      <div className="qaVizHead">
        <div className="qaVizMeta">
          <span>{translateRich("{value0} responses", {
  value0: <b>{totalResponses}</b>
})}</span>
          <span className="qaVizDot">·</span>
          <span>{translateRich("Avg {value0}", {
  value0: <b>{computedAvg != null ? `${computedAvg}%` : "—"}</b>
})}</span>
          <span className="qaVizDot">·</span>
          <span>{translateRich("{value0} blank", {
  value0: <b>{blankCount}</b>
})}</span>
        </div>

        <AppButton
          onClick={() => setSelectedBin(null)}
          disabled={!selectedBin}
          className={`qaVizClear ${selectedBin ? "" : "isDisabled"}`}
          kind="secondary"
          type="button"
        >{translate("Clear filter")}</AppButton>
      </div>

      <div className="qaVizChart">
        <ResponsiveContainer>
          <BarChart data={bins} margin={{ top: 18, right: 20, left: 0, bottom: 8 }}>
            <defs>
            <linearGradient id="scoreFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-chart-bar-fill, rgba(35, 159, 198, 0.36))" />
              <stop offset="100%" stopColor="var(--color-action-primary-soft, rgba(35, 159, 198, 0.14))" />
            </linearGradient>
            </defs>

            <XAxis
              dataKey="range"
              tick={{ fontSize: 12, fill: "var(--color-text-secondary, rgba(0,0,0,0.65))" }}
              axisLine={{ stroke: "var(--color-border-subtle, rgba(0,0,0,0.12))" }}
              tickLine={{ stroke: "var(--color-border-subtle, rgba(0,0,0,0.12))" }}
            />

            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 12, fill: "var(--color-text-secondary, rgba(0,0,0,0.65))" }}
              axisLine={{ stroke: "var(--color-border-subtle, rgba(0,0,0,0.12))" }}
              tickLine={{ stroke: "var(--color-border-subtle, rgba(0,0,0,0.12))" }}
            />

            <Tooltip
              content={<ScoreTooltip />}
              cursor={{ fill: "var(--color-bg-highlight, rgba(0,0,0,0.05))" }}
            />

            <Bar
              dataKey="count"
              radius={[14, 14, 14, 14]}
              fill="url(#scoreFill)"
              onClick={(data) => setSelectedBin({ min: data.min, max: data.max })}
              style={{ cursor: "pointer" }}
            >
              {bins.map((b) => {
                const active =
                  selectedBin &&
                  selectedBin.min === b.min &&
                  selectedBin.max === b.max;

                return (
                  <Cell
                    key={b.key}
                    fill={active ? "var(--color-action-primary, #239fc6)" : "url(#scoreFill)"}
                  />
                );
              })}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {selectedBin && (
        <div className="qaVizFoot">{translateRich("Filtering to scores in {value0}", {
  value0: <b>
            {selectedBin.min}–{selectedBin.max}
          </b>
})}</div>
      )}
    </div>
  );
}
